import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { HexAlphaColorPicker } from 'react-colorful';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import { usePreventGameKeydownListening } from '@hooks';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService from '@services/BrandingsService';
import type { IBrandingProperty } from '@interfaces/map';
import type { IBrandingEntityValues } from '@interfaces/rooms';

import {
  BrandingValueErrorCode,
  buildBrandingEntityValues,
  normalizeBrandingColorValue,
  resolveBrandingPropertyWidget,
  toStoredBrandingValue,
  validateBrandingPropertyValue,
} from '../brandingEntities';
import { sendBrandingEntitiesUpdatedToUnreal } from '../brandingSpotsUnreal';
import { useEditorMode } from '../EditorModeContext';
import { useBrandingEntityCatalogue } from '../useBrandingEntityCatalogue';
import ResourceUploadField from './ResourceUploadField';
import { useInspectorStyles } from './styles';

const AUTOSAVE_DEBOUNCE_MS = 600;

// Matches the QuickSwitcher's page: a map's configurations comfortably fit, and
// a configuration that falls outside the page reads as a failed load.
const BRANDINGS_PAGE_SIZE = 100;

const ERROR_MESSAGES: Record<
  BrandingValueErrorCode,
  { key: string; fallback: string }
> = {
  color: {
    key: 'editorMode.brandingEntityInvalidColor',
    fallback: 'Use a HEX color such as #RRGGBB or #RRGGBBAA.',
  },
  number: {
    key: 'editorMode.brandingEntityInvalidNumber',
    fallback: 'Enter a number, for example 1.5.',
  },
  integer: {
    key: 'editorMode.brandingEntityInvalidInteger',
    fallback:
      'Enter a whole number. Unreal refuses a fractional value here, so it is not rounded for you.',
  },
  boolean: {
    key: 'editorMode.brandingEntityInvalidBoolean',
    fallback: 'Use on or off.',
  },
  enum: {
    key: 'editorMode.brandingEntityInvalidEnum',
    fallback: 'Pick one of the values this build declared.',
  },
  assetPath: {
    key: 'editorMode.brandingEntityInvalidAssetPath',
    fallback: 'Enter a path starting with / or pick a declared value.',
  },
  textureUrl: {
    key: 'editorMode.brandingEntityInvalidTextureUrl',
    fallback: 'Enter an absolute http(s) URL.',
  },
};

interface IBrandingPropertyRowProps {
  property: IBrandingProperty;
  value: string;
  error: BrandingValueErrorCode | null;
  disabled: boolean;
  isClassDefault: boolean;
  onChange: (property: IBrandingProperty, next: string) => void;
}

/**
 * Renders one catalogue property with the control its declared type implies.
 *
 * @param props.property - Catalogue property being edited.
 * @param props.value - Stored override; `''` means the property is not overridden.
 * @param props.error - Failing validation rule, or `null` when the value is fine.
 * @param props.disabled - Whether editing is blocked (loading, or no configuration).
 * @param props.isClassDefault - Whether `property.default` is the class's own value
 * rather than one a level authored, which is the case for an object spawned at runtime.
 * @param props.onChange - Called with the raw value on every edit (debounced upstream).
 */
const BrandingPropertyRow = ({
  property,
  value,
  error,
  disabled,
  isClassDefault,
  onChange,
}: IBrandingPropertyRowProps) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const inputRef = usePreventGameKeydownListening<HTMLInputElement>();
  const [colorAnchorEl, setColorAnchorEl] = useState<HTMLElement | null>(null);
  // An upload in flight owns the value: clearing the override while it runs
  // would be undone the moment the URL lands, and the autosave would carry the
  // value back to the backend the author has just removed.
  const [isUploadingResource, setIsUploadingResource] = useState(false);

  const widget = resolveBrandingPropertyWidget(property);
  const { propertyId } = property;
  // Clearing an override restores `property.default`, and the wording has to say
  // where that value comes from — an object spawned at runtime is in no level to
  // restore anything from. A property declaring no default at all restores
  // nothing: a `textureUrl` never carries one, because a cooked texture has no
  // URL, so there the button only lifts the override and has to say that rather
  // than promise a value that does not exist. The hint under the control makes
  // the same three-way distinction.
  const resetLabel =
    property.default === undefined
      ? translate(
          'editorMode.brandingEntityClearOverride',
          'Clear the override',
        )
      : isClassDefault
      ? translate(
          'editorMode.brandingEntityResetToClass',
          'Restore the value the class ships',
        )
      : translate(
          'editorMode.brandingEntityReset',
          'Restore the value from the level',
        );
  const isOverridden = value !== '';
  const allowedValues = property.allowedValues ?? [];

  const stopKeyPropagation = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape') {
      event.stopPropagation();
    }
  };

  const renderTextInput = (
    testId: string,
    placeholder?: string,
    inputMode?: 'decimal' | 'numeric' | 'url' | 'text',
  ) => (
    <TextField
      inputRef={inputRef}
      fullWidth
      size="small"
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(event) => onChange(property, event.target.value)}
      onKeyDown={stopKeyPropagation}
      onKeyUp={stopKeyPropagation}
      sx={styles.textField}
      inputProps={{
        'data-testid': testId,
        ...(inputMode ? { inputMode } : {}),
      }}
    />
  );

  const renderControl = () => {
    if (widget === 'unsupported') {
      return (
        <Typography sx={styles.emptyHint}>
          {translate(
            'editorMode.brandingEntityUnsupportedType',
            'This version of the hub cannot edit this value type yet.',
          )}
        </Typography>
      );
    }

    if (widget === 'colorPicker') {
      const swatchColor = normalizeBrandingColorValue(value) ?? 'transparent';

      return (
        <>
          <Box sx={styles.colorRow}>
            <Box
              sx={[
                styles.colorSwatch,
                { backgroundColor: swatchColor },
                !isOverridden && styles.spotThumbPlaceholder,
              ]}
              onClick={(event) => {
                if (!disabled) {
                  setColorAnchorEl(event.currentTarget);
                }
              }}
              data-testid={`branding-entity-${propertyId}-color-swatch`}
            />
            <Box sx={styles.colorRowText}>
              <TextField
                inputRef={inputRef}
                fullWidth
                size="small"
                value={value}
                disabled={disabled}
                placeholder={property.default ?? '#RRGGBBAA'}
                onChange={(event) => onChange(property, event.target.value)}
                onKeyDown={stopKeyPropagation}
                onKeyUp={stopKeyPropagation}
                sx={styles.pickerHexField}
                inputProps={{
                  maxLength: 9,
                  'data-testid': `branding-entity-${propertyId}-color-input`,
                }}
              />
            </Box>
          </Box>
          <Popover
            open={Boolean(colorAnchorEl)}
            anchorEl={colorAnchorEl}
            onClose={() => setColorAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{ sx: styles.pickerPopover }}
          >
            <HexAlphaColorPicker
              color={normalizeBrandingColorValue(value) ?? '#ffffffff'}
              onChange={(next) => onChange(property, next)}
            />
          </Popover>
        </>
      );
    }

    if (widget === 'toggle') {
      // A switch has no "not overridden" position, so it shows the value that
      // is actually in effect — the override when there is one, the level
      // default otherwise — and the reset button removes the override.
      const effective = isOverridden ? value : property.default ?? 'false';

      return (
        <Switch
          size="small"
          inputRef={inputRef}
          checked={effective === 'true'}
          disabled={disabled}
          onKeyDown={stopKeyPropagation}
          onKeyUp={stopKeyPropagation}
          onChange={(event) =>
            onChange(property, event.target.checked ? 'true' : 'false')
          }
          inputProps={
            {
              'data-testid': `branding-entity-${propertyId}-toggle`,
            } as React.InputHTMLAttributes<HTMLInputElement>
          }
        />
      );
    }

    if (widget === 'select') {
      if (allowedValues.length === 0) {
        return (
          <Typography sx={styles.emptyHint}>
            {translate(
              'editorMode.brandingEntityNoAllowedValues',
              'This build declared no values for this property.',
            )}
          </Typography>
        );
      }

      return (
        <Select
          fullWidth
          size="small"
          displayEmpty
          value={allowedValues.includes(value) ? value : ''}
          disabled={disabled}
          onKeyDown={stopKeyPropagation}
          onKeyUp={stopKeyPropagation}
          onChange={(event) => onChange(property, event.target.value)}
          sx={styles.textField}
          SelectDisplayProps={
            {
              'data-testid': `branding-entity-${propertyId}-select`,
            } as React.HTMLAttributes<HTMLDivElement>
          }
          MenuProps={{
            MenuListProps: {
              'data-testid': `branding-entity-${propertyId}-select-list`,
            } as React.HTMLAttributes<HTMLUListElement>,
          }}
        >
          <MenuItem
            value=""
            data-testid={`branding-entity-${propertyId}-option-authored`}
          >
            {translate(
              'editorMode.brandingEntityUseAuthored',
              'Use the value from the level',
            )}
          </MenuItem>
          {allowedValues.map((allowed) => (
            <MenuItem
              key={allowed}
              value={allowed}
              data-testid={`branding-entity-${propertyId}-option-${allowed}`}
            >
              {allowed}
            </MenuItem>
          ))}
        </Select>
      );
    }

    if (widget === 'numberField') {
      return renderTextInput(
        `branding-entity-${propertyId}-number-input`,
        property.default,
        'decimal',
      );
    }

    if (widget === 'integerField') {
      return renderTextInput(
        `branding-entity-${propertyId}-number-input`,
        property.default,
        'numeric',
      );
    }

    if (widget === 'urlField') {
      // A texture is a file, not an address: the row uploads it and stores the
      // URL it lands on, through the same `onChange` a typed URL went through.
      return (
        <ResourceUploadField
          value={value}
          onChange={(next) => onChange(property, next)}
          accept="image/png,image/jpeg"
          disabled={disabled}
          testIdPrefix={`branding-entity-${propertyId}`}
          uploadLabel={translate(
            'editorMode.brandingEntityUpload',
            'Upload a PNG or JPEG',
          )}
          unsupportedLabel={translate(
            'editorMode.brandingEntityUploadUnsupported',
            'Use a PNG or JPEG image.',
          )}
          onUploadingChange={setIsUploadingResource}
        />
      );
    }

    if (widget === 'pathField') {
      return renderTextInput(
        `branding-entity-${propertyId}-path-input`,
        property.default ?? '/Game/…',
        'text',
      );
    }

    return renderTextInput(
      `branding-entity-${propertyId}-text-input`,
      property.default,
      'text',
    );
  };

  return (
    <Box
      sx={styles.brandingPropertyRow}
      data-testid={`branding-entity-property-row-${propertyId}`}
    >
      <Box sx={styles.brandingPropertyHeader}>
        <Tooltip title={propertyId} placement="bottom" enterDelay={400}>
          <Typography sx={styles.brandingPropertyLabel}>
            {property.displayName || propertyId}
          </Typography>
        </Tooltip>
        <Box component="span" sx={styles.brandingPropertyTypeChip}>
          {property.type}
        </Box>
        {isOverridden && widget !== 'unsupported' && (
          <Tooltip title={resetLabel} placement="bottom" enterDelay={400}>
            <IconButton
              size="small"
              disabled={disabled || isUploadingResource}
              onClick={() => onChange(property, '')}
              sx={styles.brandingPropertyResetButton}
              aria-label={resetLabel}
              data-testid={`branding-entity-${propertyId}-reset-btn`}
            >
              <RestartAltOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {renderControl()}

      <Typography sx={styles.brandingPropertyHint}>
        {property.default === undefined
          ? translate(
              isClassDefault
                ? 'editorMode.brandingEntityNoClassDefault'
                : 'editorMode.brandingEntityNoDefault',
              isClassDefault ? 'No class default' : 'No level default',
            )
          : translate(
              isClassDefault
                ? 'editorMode.brandingEntityClassDefault'
                : 'editorMode.brandingEntityDefault',
              isClassDefault
                ? 'Class default: {{value}}'
                : 'Level default: {{value}}',
              {
                value: property.default,
              },
            )}
      </Typography>
      {property.authoredAssetPath && (
        <Typography sx={styles.brandingPropertyHint}>
          {translate(
            'editorMode.brandingEntityAuthoredAsset',
            'Authored asset: {{value}}',
            { value: property.authoredAssetPath },
          )}
        </Typography>
      )}
      {error && (
        <Typography sx={styles.helperError}>
          {translate(ERROR_MESSAGES[error].key, ERROR_MESSAGES[error].fallback)}
        </Typography>
      )}
    </Box>
  );
};

interface IBrandingEntityInspectorProps {
  entityId: string;
}

/**
 * Renders the editor for one brandable entity the current build declares.
 *
 * Saves are debounced and always carry the entity's complete property set, so
 * the backend's per-entity replace never drops a property the user did not
 * touch; the same set is pushed to Unreal once per save.
 *
 * @param props.entityId - Catalogue entity being edited.
 */
const BrandingEntityInspector = ({
  entityId,
}: IBrandingEntityInspectorProps) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const {
    setAutosaveStatus,
    brandingEntitiesTick,
    registerBrandingEntityFlush,
  } = useEditorMode();
  const { sendMessageToUnreal } = useMytaverse();
  const { currentRoom, currentBrandingId, isBrandingModeEnabled } =
    useMytaverseEvent();

  const mapId = currentRoom?.map?.mapId;
  const isEditingLive =
    !!currentBrandingId && currentBrandingId === currentRoom?.activeBrandingId;

  const { entities: brandingEntities } = useBrandingEntityCatalogue();
  // The properties are read off whichever source this entity came from, so the
  // "level default" a row shows and the value its reset restores are already the
  // right ones: the actor's authored value for an object placed in the map, the
  // class default for one spawned at runtime.
  const entity = useMemo(
    () =>
      brandingEntities.find((candidate) => candidate.entityId === entityId) ??
      null,
    [brandingEntities, entityId],
  );

  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<
    Record<string, BrandingValueErrorCode | null>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadedRef = useRef(false);
  // The save the debounce is holding. Kept in a ref so it can still be flushed
  // from a cleanup, which runs with a stale closure over state.
  const pendingSaveRef = useRef<{
    brandingId: string;
    values: Record<string, string>;
  } | null>(null);
  // Properties edited in this session; only these are held against a save, so a
  // stored value this version considers invalid cannot lock the panel.
  const touchedRef = useRef<Set<string>>(new Set());
  // The schema this panel was last opened on. A live object can leave the world
  // inside the 600 ms the save is debounced for, which drops it from the
  // catalogue and would leave the flush with no property to normalize against —
  // the value the user typed would then be stored raw.
  const propertiesRef = useRef<IBrandingProperty[]>([]);

  useEffect(() => {
    if (entity?.properties?.length) {
      propertiesRef.current = entity.properties;
    }
  }, [entity?.properties]);

  const persist = useCallback(
    async (brandingId: string, nextValues: Record<string, string>) => {
      const propertiesById = new Map(
        (entity?.properties?.length
          ? entity.properties
          : propertiesRef.current
        ).map((property) => [property.propertyId, property]),
      );

      const stored: Record<string, string> = {};
      Object.entries(nextValues).forEach(([propertyId, raw]) => {
        const property = propertiesById.get(propertyId);
        stored[propertyId] = property
          ? toStoredBrandingValue(property, raw)
          : raw.trim();
      });

      const brandingEntities: IBrandingEntityValues = buildBrandingEntityValues(
        entityId,
        stored,
      );

      setAutosaveStatus('saving');
      try {
        const updated = await BrandingsService.updateBranding(brandingId, {
          brandingEntities,
        });

        // Unreal is told what was stored, not what was sent: an uploaded
        // texture is relocated while it is persisted, and its temporary URL
        // stops resolving the moment that happens.
        const persistedValues = updated?.brandingEntities?.[entityId];
        const appliedEntities: IBrandingEntityValues = persistedValues
          ? { [entityId]: persistedValues }
          : brandingEntities;

        if (isBrandingModeEnabled) {
          sendBrandingEntitiesUpdatedToUnreal(
            sendMessageToUnreal,
            appliedEntities,
          );
        }

        // Adopt the stored values, unless the user has typed since — then their
        // edit is newer and the next save will reconcile it.
        if (persistedValues && !debounceRef.current) {
          setValues({ ...persistedValues });
        }

        setAutosaveStatus('saved');
      } catch (saveError) {
        console.error(
          `Failed to save branding entity values: ${
            (saveError as Error)?.message
          }`,
          { brandingId, entityId },
        );
        setAutosaveStatus('error');
      }
    },
    [
      entity?.properties,
      entityId,
      isBrandingModeEnabled,
      sendMessageToUnreal,
      setAutosaveStatus,
    ],
  );

  /**
   * Sends the save the debounce is still holding, if any.
   *
   * Called before the panel stops watching a configuration — on unmount and on a
   * configuration switch — so an edit made in the last 600 ms is not lost, and a
   * timer belonging to the previous configuration cannot fire after the switch.
   *
   * @returns Resolves once the held save has been written, so a caller about to
   * write the same record can wait for it rather than race it.
   */
  const flushPendingSave = useCallback(async () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    const pending = pendingSaveRef.current;
    pendingSaveRef.current = null;

    if (pending) {
      await persist(pending.brandingId, pending.values);
    }
  }, [persist]);

  // The CSV import writes the same record from outside this panel, so it has to
  // be able to settle a held edit first.
  useEffect(() => {
    registerBrandingEntityFlush(flushPendingSave);

    return () => {
      registerBrandingEntityFlush(null);
    };
  }, [flushPendingSave, registerBrandingEntityFlush]);

  useEffect(() => {
    if (!currentBrandingId || !mapId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setHasLoadFailed(false);
    initialLoadedRef.current = false;
    touchedRef.current = new Set();

    const load = async () => {
      try {
        const response = await BrandingsService.getBrandings({
          mapId,
          limit: BRANDINGS_PAGE_SIZE,
        });
        if (cancelled) {
          return;
        }

        const branding = response.brandings?.find(
          (candidate) => candidate?.brandingId === currentBrandingId,
        );

        // Editing without the stored values would replace the whole entity with
        // whatever single property gets touched, so a configuration that did not
        // come back is treated as a failed load rather than as "no overrides".
        if (!branding) {
          throw new Error(
            `Configuration ${currentBrandingId} not found for map ${mapId}`,
          );
        }

        setValues({ ...(branding.brandingEntities?.[entityId] ?? {}) });
        setErrors({});
        initialLoadedRef.current = true;
      } catch (loadError) {
        if (cancelled) {
          return;
        }

        console.error(
          `Failed to load branding entity values: ${
            (loadError as Error)?.message
          }`,
          { brandingId: currentBrandingId, entityId },
        );
        setHasLoadFailed(true);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      // The values being replaced belong to the configuration being left.
      flushPendingSave();
    };
    // `brandingEntitiesTick` is what makes an import land in an open panel: the
    // values it wrote are read back rather than left stale on screen.
  }, [
    currentBrandingId,
    mapId,
    entityId,
    flushPendingSave,
    brandingEntitiesTick,
  ]);

  const handleChange = useCallback(
    (property: IBrandingProperty, next: string) => {
      const nextValues = { ...values, [property.propertyId]: next };
      setValues(nextValues);
      touchedRef.current.add(property.propertyId);

      const nextErrors: Record<string, BrandingValueErrorCode | null> = {};
      for (const candidate of entity?.properties ?? []) {
        if (!touchedRef.current.has(candidate.propertyId)) {
          continue;
        }

        nextErrors[candidate.propertyId] = validateBrandingPropertyValue(
          candidate,
          nextValues[candidate.propertyId] ?? '',
        );
      }
      setErrors(nextErrors);

      if (!initialLoadedRef.current || !currentBrandingId) {
        return;
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      // A save carries the entity's whole set, so one invalid value would ride
      // along with the others and Unreal would refuse it. Nothing is sent while
      // any edited property is invalid; the rows say which.
      if (Object.values(nextErrors).some((errorCode) => !!errorCode)) {
        pendingSaveRef.current = null;
        return;
      }

      pendingSaveRef.current = {
        brandingId: currentBrandingId,
        values: nextValues,
      };

      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        flushPendingSave();
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [currentBrandingId, entity?.properties, flushPendingSave, values],
  );

  if (!entity) {
    return (
      <Typography sx={styles.emptyHint}>
        {translate(
          'editorMode.brandingEntityMissing',
          'This object is no longer declared by the active build.',
        )}
      </Typography>
    );
  }

  const disabled = isLoading || hasLoadFailed || !currentBrandingId;

  return (
    <Box>
      {isEditingLive && (
        <Box sx={styles.liveWarning}>
          <WarningAmberOutlinedIcon sx={{ fontSize: 14, color: '#FFB800' }} />
          <Typography sx={styles.liveWarningText}>
            {translate('editorMode.liveBrandingWarning')}
          </Typography>
        </Box>
      )}

      <Box sx={styles.section}>
        <Box sx={styles.brandingEntityMeta}>
          <Typography sx={styles.brandingEntityMetaText}>
            {translate('editorMode.brandingEntityType', 'Kind: {{value}}', {
              value: entity.entityType,
            })}
          </Typography>
          {/* An object that only exists once the session runs sits in no level of
              the build, so its `maps` is empty by construction (see the live branch
              of getBrandingEntityCatalogue) — and a bare "Visible on:" with nothing
              after it reads as a value that failed to load rather than as a question
              that does not apply to this row. */}
          {entity.maps.length > 0 && (
            <Typography sx={styles.brandingEntityMetaText}>
              {translate(
                'editorMode.brandingEntityMaps',
                'Visible on: {{value}}',
                { value: entity.maps.join(', ') },
              )}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={styles.section}>
        <Typography sx={styles.sectionLabel}>
          {translate('editorMode.brandingEntityProperties', 'Properties')}
        </Typography>
        {hasLoadFailed && (
          <Typography sx={styles.helperError}>
            {translate(
              'editorMode.brandingEntityLoadFailed',
              'Could not load the saved values. Editing is disabled so nothing is overwritten — reopen the object to retry.',
            )}
          </Typography>
        )}
        {entity.properties.length === 0 ? (
          <Typography sx={styles.emptyHint}>
            {translate(
              'editorMode.brandingEntityNoProperties',
              'This object declares nothing to edit.',
            )}
          </Typography>
        ) : (
          entity.properties.map((property) => (
            <BrandingPropertyRow
              key={property.propertyId}
              property={property}
              value={values[property.propertyId] ?? ''}
              error={errors[property.propertyId] ?? null}
              disabled={disabled}
              isClassDefault={entity?.defaultSource === 'class'}
              onChange={handleChange}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default BrandingEntityInspector;
